/**
 * SSO config API client — talks to sf-auth-service `/identity/sso-config`.
 *
 * ## Why not sf-api?
 * SF-3179 initially introduced a company-scoped sf-api proxy but it was
 * removed in commit `702c3817 refactor(SF-3179): remove sso-config proxy
 * from sf-api` — SSO config CRUD is intentionally a direct
 * sf-auth-service admin surface. The routes are mounted at
 * `/identity/sso-config` in sf-auth-service (`src/index.ts`) and are
 * gated server-side by `admin:sso:read` / `admin:sso:write`. The admin
 * app holds a sf-auth-service access token already (for /auth/me,
 * /auth/google, etc.), so talking directly keeps the blast radius
 * identical to every other admin surface.
 *
 * ## Client-side company filter
 * sf-auth-service has no `GET /identity/sso-config?companyId=...`
 * filter and no `GET /identity/sso-config/by-company/:id` shortcut
 * (yet). We therefore list and filter locally. This stays correct for
 * the early rollout (one config per onboarded customer, low tenant
 * count). If the collection grows, the right fix is a server-side
 * filter on sf-auth-service, NOT a new sf-api proxy — follow-up
 * ticket SF-3460 tracks that.
 *
 * ## Permission gating
 * The admin app also hides the SSO tab upfront via `useCan({resource:
 * 'sso', action: 'show'})` (AC8) so unauthorised users never issue the
 * GET. The server-side 403 guard is still enforced as the authority
 * if a request somehow reaches it.
 *
 * SF-3404 froze the payload shape — see `./types.ts`.
 *
 * Secret handling: GET always returns `clientSecret: '***'`. POST/PATCH
 * only carry `clientSecret` when the operator supplies a new value —
 * the caller is responsible for omitting the field otherwise so the
 * stored secret is preserved.
 */
import type { AxiosInstance } from 'axios';
import type { SsoConfig } from './types';

const BASE = '/identity/sso-config';

/**
 * Fetch the SSO config for a given company. Returns `null` if no config
 * exists yet (expected for companies that haven't been onboarded to SSO).
 *
 * See the module header for why we list and filter client-side rather
 * than hitting a company-scoped endpoint — that surface does not exist
 * on sf-auth-service, and the removed sf-api proxy is not coming back.
 */
export async function getCompanySsoConfig(
  client: AxiosInstance,
  companyId: string
): Promise<SsoConfig | null> {
  const { data } = await client.get<{ ssoConfigs: SsoConfig[] }>(BASE);
  const list = Array.isArray(data?.ssoConfigs) ? data.ssoConfigs : [];
  const match = list.find((c) => c.companyId === companyId);
  return match ?? null;
}

/**
 * POST a new SSO config (create flow, AC2). The server assigns the id
 * and returns the masked document.
 */
export async function createSsoConfig(
  client: AxiosInstance,
  payload: Partial<SsoConfig>
): Promise<SsoConfig> {
  const { data } = await client.post<{ ssoConfig: SsoConfig }>(BASE, payload);
  return data.ssoConfig;
}

/**
 * PATCH an existing SSO config. Callers build the payload using the form's
 * `buildPatchPayload` so we never send the masked secret back to the server.
 * Returns the freshly formatted doc (with `clientSecret` masked).
 *
 * Not wired to a UI action yet — Task 3 surfaces the save button. Kept here
 * so the shape is fixed up-front and Task 3 is a wiring change, not a new
 * contract.
 */
export async function patchSsoConfig(
  client: AxiosInstance,
  id: string,
  payload: Partial<SsoConfig>
): Promise<SsoConfig> {
  const { data } = await client.patch<{ ssoConfig: SsoConfig }>(`${BASE}/${id}`, payload);
  return data.ssoConfig;
}

/**
 * DELETE an SSO config. Server audit-logs the action (see SF-3404 DELETE
 * handler). Irreversible — the tab gates this behind an AntD Popconfirm
 * (AC7).
 */
export async function deleteSsoConfig(
  client: AxiosInstance,
  id: string
): Promise<void> {
  await client.delete(`${BASE}/${id}`);
}
